import { selector } from '@/lib/selectors';
import { CachedPaymentFactory } from '@/domain/payments/factories';
import { providerApproversSelector } from '@/domain/paySettings';
import { UserEnhancedApprovalFactory } from '@/domain/approvals';
import { userIdSelector } from '@/domain/env';
import { PAYMENT_STATUSES } from '@/domain/payments/constants';

export const payments = (state) => state.payments;

export const paymentsByIdSelector = selector(payments, (p) => p.list);
export const paymentsListListSelector = selector(paymentsByIdSelector, (p) => p.toList());
export const loadedSelector = selector(payments, (p) => p.loaded);
export const paymentsGridLoaderSelector = selector(payments, (p) => p.gridLoader);
export const paymentsGridHeadersListSelector = selector(payments, (p) => p.gridHeadersList);
export const paymentsCountSelector = selector(payments, (p) => p.count);
export const paymentsTotalByStateSelector = selector(payments, (p) => p.totalByState);
export const savedGridFiltersSelector = selector(payments, (p) => p.gridFilters || {});
export const savedGridSortingSelector = selector(payments, (p) => p.gridSorting || []);
export const savedGridColumnsSelector = selector(payments, (p) => p.gridColumns);
export const gridColumnsScopeSelector = selector(payments, (p) => p.gridColumnsScope);
export const getGridPaymentsByPageIndexSelector = selector(payments, (p) => p.gridPaymentsByPageIndex);
export const getGridSearchAfterByPageIndexSelector = selector(payments, (p) => p.gridSearchAfterByPageIndex);
export const savedGridDefaultColumnsSelector = selector(payments, (p) => p.gridDefaultColumns);
// flag determines when server-side data needs to be updated
// payments List that need to be updated - after bulk actions
export const gridApplyTransactionSelector = selector(payments, (p) => p.gridApplyTransaction);

export const gridCurrentColumnsStateSelector = selector(
  savedGridDefaultColumnsSelector,
  savedGridColumnsSelector,
  (defaultGridColumnsState, changedGridColumnsState) => {
    return changedGridColumnsState || defaultGridColumnsState || null;
  },
);

export const paymentSelector = selector(payments, (p) => p.payment);
export const paymentInfoSelector = selector(paymentSelector, (p) => p.info);
export const paymentPaymentIdSelector = selector(paymentInfoSelector, (i) => i.id);
export const isReadOnlyPaymentSelector = selector(
  paymentInfoSelector,
  (i) => i.status !== PAYMENT_STATUSES.PREPARATION,
);
export const paymentMetaSelector = selector(paymentSelector, (p) => p.meta);
export const isPaymentMetaRefreshingSelector = selector(payments, (p) => p.metaRefreshing);
export const paymentInvoicesSelector = selector(paymentSelector, (p) => p.invoices);
export const paymentWalletBalanceSelector = selector(paymentSelector, (p) => p.balance);
export const paymentApprovalsSelector = selector(paymentSelector, (p) => p.approvals);
export const paymentApprovalsLevelsSelector = selector(paymentApprovalsSelector, (a) => a.levels);
export const paymentTagsSelector = selector(paymentSelector, (p) => p.tags);

export const paymentNavigationSelector = selector(payments, (p) => p.get('navigation'));
export const cachedPaymentSelector = (paymentHash) =>
  selector(payments, (p) => p.cachedPayments.find(({ hash }) => hash === paymentHash) || CachedPaymentFactory());

export const paymentApprovalsLevelsAdaptedSelector = selector(
  paymentApprovalsLevelsSelector,
  providerApproversSelector,
  (levels, approversList) =>
    levels
      .filter(({ status }) => status !== 'inactive')
      .map(({ approvers, minApprovers, minAmount, flowType, status }) => ({
        approvers: approvers.map((approval) => {
          const approver = approversList.find((u) => u.username === approval.username);
          return UserEnhancedApprovalFactory({
            userGuid: approval.username,
            tag: approval.username,
            id: approval.account_user_id,
            picture: approver?.avatar,
            status: approval.status,
            fullName: approver?.fullName || approval.username,
          }).toJS();
        }),
        minApprovers,
        minAmount,
        flowType,
        status,
      })),
);

export const paymentApprovalRejectReasonsSelector = selector(
  paymentApprovalsSelector,
  providerApproversSelector,
  (a, approversList) =>
    a.rejectReasons.map(({ message, username }) => {
      const approver = approversList.find((u) => u.username === username);
      return { message, username, fullName: approver?.fullName || username };
    }),
);
export const isApprovalFlowActiveSelector = selector(paymentApprovalsSelector, (a) => a.status === 'active');
export const shouldCurrentUserApproveSelector = selector(
  paymentApprovalsLevelsSelector,
  userIdSelector,
  isApprovalFlowActiveSelector,
  (levels, currentUserId, isActiveApprovalFlow) => {
    const pendingLevel = levels.find((level) => level.status === 'pending');
    const pendingUsernames = pendingLevel
      ? pendingLevel.approvers.filter((approver) => approver.status === 'pending').map((approver) => approver.username)
      : [];

    return pendingUsernames.includes(currentUserId) && isActiveApprovalFlow;
  },
);

export const isApprovalRequiredSelector = selector(
  paymentApprovalsLevelsSelector,
  (levels) => !(levels.length === 0 || levels.every(({ status }) => status === 'inactive')),
);
